import { Schema } from 'rsuite';
import {
  set,
  get,
  getOr,
  reduce,
  isEmpty,
  isArray,
  range,
  keys,
  includes,
  pick
} from 'lodash/fp';
import moment from 'moment';
import { toStandardDate } from '../../../helpers/dates';
import { CurrentLocation } from '../../../helpers/config';
import { parseInt } from '../../../helpers/utils';

const reduceWithKey = reduce.convert({ cap: false });
const { DateType, StringType, NumberType, ArrayType } = Schema.Types;

export const getValidatorForFieldType = type => {
  switch (type) {
    case 'date':
      return DateType();
    case 'email':
      return StringType().isEmail('Invalid Email');
    case 'checkbox':
      return ArrayType(NumberType());
    case 'bool':
    case 'number':
    case 'guest_relation':
      return NumberType();
    default:
      return StringType();
  }
};

export const contactNumberValidation = () =>
  StringType().maxLength(20, 'Maximum of 20 characters');

export const createConditionalRequiredRule = (validator, isRequired) => {
  if (isRequired) return validator.isRequired('Required field');
  return validator;
};

export const DefaultValues = {
  firstname: '',
  middlename: '',
  lastname: '',
  email: '',
  homeless: '0',
  blackball: '0',
  street_address: '',
  apartment: '',
  city: '',
  state: '',
  zipCode: '',
  phone: '',
  workphone: '',
  cellphone: '',
  household_total: 1,
  location_id: CurrentLocation
};

export const composeAgeDefaultValues = ageGroupCount => {
  if (!ageGroupCount) return {};

  return reduce(
    (fields, index) => set(`age${index + 1}`, 0, fields),
    {},
    range(0, ageGroupCount)
  );
};

export const showFieldsPerSettings = (settings, isHomeless) => ({
  street_address: !isHomeless,
  apartment: !isHomeless,
  city: !isHomeless,
  state: !isHomeless,
  zipcode: !isHomeless,
  county: !isHomeless && settings.guestAdditionalCounty === '1',
  email: settings.guestRequireEmail === '1',
  phone: settings.guestShowHomePhone === '1',
  workphone: settings.guestShowWorkPhone === '1',
  cellphone: settings.guestShowCellPhone === '1',
  location_id: settings.guestAdditionalLocation === '1',
  spouse: settings.guestSpouse === '1',
  marital_status: settings.guestMaritalStatus === '1',
  language: settings.guestLanguage === '1',
  educationLast: settings.guestAdditionalEducationLast === '1',
  driverLicence: settings.guestAdditionalGDLNum === '1',
  driverLicenceState: settings.guestAdditionalGDLS === '1',
  employer: settings.guestAdditionalGEmployer === '1',
  spouseDriverLicence: settings.guestAdditionalSDLNum === '1',
  spouseDriverLicenceState: settings.guestAdditionalSDLS === '1',
  spouseEmployer: settings.guestAdditionalSEmployer === '1',
  gender: settings.guestAdditionalGender === '1',
  education: settings.guestAdditionalEducation === '1',
  race: settings.guestAdditionalRace === '1',
  contactForHelpWhy: settings.guestAdditionalContactHelp === '1',
  reason: settings.guestAdditionalReasons === '1'
});

const requiredAgeFieldsPerSettings = ({ ageGroupCount, ...settings }) => {
  if (!ageGroupCount) return {};

  const isAgeBreakdown = settings.guestAdditionalOthersAtHome === '2';
  const isEnabledAgeBreakdown = settings.guestOtherAgeHH === '1';
  const isRequiredAgeBreakdown = settings.guestOtherAgeReq === '1';
  const requireAgeFields =
    isAgeBreakdown && isEnabledAgeBreakdown && isRequiredAgeBreakdown;

  if (!requireAgeFields) return {};

  return reduce(
    (fields, index) => set(`age${index + 1}`, true, fields),
    {},
    range(0, ageGroupCount)
  );
};

export const requiredFieldsPerSettings = (settings, fields, isContactForHelp) =>
  pick(fields, {
    ...requiredAgeFieldsPerSettings(settings),
    household_total: true,
    email: settings.guestRequireEmail === '2',
    firstname: settings.guestRequireFirstName === '1',
    middlename: settings.guestRequireMiddleName === '1',
    lastname: settings.guestRequireLastName === '1',
    dob: settings.guestRequireDOB === '1',
    street_address: settings.guestRequireStreetAddress === '1',
    city: settings.guestRequireCity === '1',
    state: settings.guestRequireState === '1',
    zipcode: settings.guestRequireZip === '1',
    county: settings.guestAdditionalCountyReq === '1',
    phone: settings.guestRequireHomePhone === '1',
    workphone: settings.guestRequireWorkPhone === '1',
    cellphone: settings.guestRequireCellPhone === '1',
    educationLast: settings.guestAdditionalEducationReq === '1',
    driverLicence: settings.guestAdditionalGDLNumReq === '1',
    driverLicenceState: settings.guestAdditionalGDLSReq === '1',
    employer: settings.guestAdditionalGEmployerReq === '1',
    spouseDriverLicence: settings.guestAdditionalSDLNumReq === '1',
    spouseDriverLicenceState: settings.guestAdditionalSDLSReq === '1',
    spouseEmployer: settings.guestAdditionalSEmployerReq === '1',
    gender: settings.guestAdditionalGenderReq === '1',
    education: settings.guestAdditionalEducationReq === '1',
    race: settings.guestAdditionalRaceReq === '1',
    contactForHelp: settings.guestAdditionalContactHelpReq === '1',
    contactForHelpWhy: isContactForHelp,
    reason: settings.guestAdditionalReasonsReq === '1'
  });

export const createModelSchema = ({
  formFields,
  requiredFields,
  isHomeless,
  isAgeBreakdown
}) => {
  const addressValidation = !isHomeless
    ? {
        street_address: createConditionalRequiredRule(
          StringType(),
          requiredFields.street_address
        ),
        apartment: StringType(),
        city: createConditionalRequiredRule(StringType(), requiredFields.city),
        state: createConditionalRequiredRule(
          StringType(),
          requiredFields.state
        ),
        zipcode: createConditionalRequiredRule(
          StringType().pattern(
            /^[a-zA-Z0-9 ]{1,9}$/,
            'Only accepts numbers, letters and spaces - maximum of 9 characters'
          ),
          requiredFields.zipcode
        ),
        county: createConditionalRequiredRule(
          StringType(),
          requiredFields.county
        )
      }
    : {};

  const mainFieldsSchema = {
    firstname: createConditionalRequiredRule(StringType(), true),
    middlename: createConditionalRequiredRule(
      StringType(),
      requiredFields.middlename
    ),
    lastname: createConditionalRequiredRule(
      StringType(),
      requiredFields.lastname
    ),
    dob: createConditionalRequiredRule(DateType(), requiredFields.dob),
    homeless: NumberType().isRequired('Required field'),
    ...addressValidation,
    household_total: NumberType('Invalid numeric value')
      .addRule((value, data) => {
        if (isAgeBreakdown) {
          const ageSum = reduceWithKey(
            (result, val, field) =>
              field.substring(0, 3) === 'age' && val
                ? result + parseInt(val)
                : result,
            0,
            data
          );
          return parseInt(value) === ageSum;
        }
        return true;
      }, 'Does not match age breakdown values')
      .isRequired('Required Field'),
    email: createConditionalRequiredRule(
      StringType().isEmail('Invalid Email'),
      requiredFields.email
    ),
    phone: createConditionalRequiredRule(
      contactNumberValidation(),
      requiredFields.phone
    ),
    workphone: createConditionalRequiredRule(
      contactNumberValidation(),
      requiredFields.workphone
    ),
    cellphone: createConditionalRequiredRule(
      contactNumberValidation(),
      requiredFields.cellphone
    ),
    educationLast: createConditionalRequiredRule(
      StringType(),
      requiredFields.educationLast
    ),
    driverLicence: createConditionalRequiredRule(
      StringType(),
      requiredFields.driverLicence
    ),
    driverLicenceState: createConditionalRequiredRule(
      StringType(),
      requiredFields.driverLicenceState
    ),
    employer: createConditionalRequiredRule(
      StringType(),
      requiredFields.employer
    ),
    spouseDriverLicence: createConditionalRequiredRule(
      StringType(),
      requiredFields.spouseDriverLicence
    ),
    spouseDriverLicenceState: createConditionalRequiredRule(
      StringType(),
      requiredFields.spouseDriverLicenceState
    ),
    spouseEmployer: createConditionalRequiredRule(
      StringType(),
      requiredFields.spouseEmployer
    ),
    language: createConditionalRequiredRule(
      StringType(),
      requiredFields.language
    ),
    gender: createConditionalRequiredRule(NumberType(), requiredFields.gender),
    education: createConditionalRequiredRule(
      NumberType(),
      requiredFields.education
    ),
    race: createConditionalRequiredRule(StringType(), requiredFields.race),
    contactForHelp: createConditionalRequiredRule(
      NumberType(),
      requiredFields.contactForHelp
    ),
    contactForHelpWhy: createConditionalRequiredRule(
      StringType(),
      requiredFields.contactForHelpWhy
    ),
    reason: createConditionalRequiredRule(NumberType(), requiredFields.reason)
  };

  const dynamicFieldsSchema = reduce(
    (allFields, { name, fieldType, isRequired = false }) => {
      const isRequiredField = getOr(isRequired, name, requiredFields);

      const isAgeField = name.substring(0, 3) === 'age';
      if (isAgeField && isAgeBreakdown) {
        return {
          ...allFields,
          [name]: createConditionalRequiredRule(
            NumberType().min(0, 'Minimum value is 0'),
            isRequiredField
          )
        };
      }

      const isCustomField = name.substring(0, 3) === 'cf_';
      if (isCustomField) {
        return {
          ...allFields,
          [name]: createConditionalRequiredRule(
            getValidatorForFieldType(fieldType),
            isRequiredField
          )
        };
      }
      return allFields;
    },
    {},
    formFields
  );

  return Schema.Model(
    pick(keys(formFields), { ...mainFieldsSchema, ...dynamicFieldsSchema })
  );
};

export const dataToFormValue = (data, formFields) =>
  reduceWithKey(
    (result, value, field) => {
      const fieldType = get(`${field}.fieldType`, formFields);
      switch (fieldType) {
        case 'date':
          return set(
            field,
            moment(value).isValid() ? moment(value).toDate() : null,
            result
          );
        case 'checkbox':
          return set(field, value || [], result);
        case 'bool':
          return set(field, value, result);
        default:
          return set(field, value || '', result);
      }
    },
    {},
    data
  );

const bodyExcludedFields = [
  'snap_id',
  'addrUpdated',
  'dobSet',
  'created_at',
  'updated_at',
  'othersHousehold',
  'noMatch',
  'profilePicture',
  'saveCount',
  'system_id',
  'visit_exception',
  'visitor_type'
];
export const formValueToBody = (formValue, fieldSettings) =>
  reduceWithKey(
    (values, value, field) => {
      if (includes(bodyExcludedFields, field)) return values;
      // Skip read-only / disabled fields (e.g. Age Breakdown under Method 1)
      if (get(`${field}.disabled`, fieldSettings)) return values;
      const fieldType = get(`${field}.fieldType`, fieldSettings);
      if (fieldType === 'date' && value) {
        const dateVal = moment(value).isValid() ? toStandardDate(value) : null;
        return set(field, dateVal, values);
      }
      return set(field, value, values);
    },
    {},
    formValue
  );

export const toFieldOptions = options => {
  if (isEmpty(options) || !options) return [];
  if (isArray(options))
    return options.map((label, value) => ({ label, value }));

  return reduceWithKey(
    (all, label, value) => [...all, { label, value }],
    [],
    options
  );
};
